import {
  faArrowLeft,
  faList,
  faPlay,
  faSlidersH,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { B2Button, B2Form } from "@hdb/biome-2";
import { Form } from "react-bootstrap";
import { FloorAnalyticsView } from "src/core/enums/floor-analytics-enums";
import DashboardFloorplanWildcardSearchingComponent from "../../../dashboard-floorplan-wildcard-searching.component";

interface AnalyticsHeaderProps {
  viewType: FloorAnalyticsView;
  onViewTypeChange: (value: FloorAnalyticsView) => void;
  date: string;
  onDateChange: (date: string) => void;
  minDate: string;
  maxDate: string;
  selectedAssetCount: number;
  isSelectedListVisible: boolean;
  onToggleSelectedList: () => void;
  onCompare: () => void;
  maxHourRange: number;
  hourRange: number;
  onHourRangeChange: (hour: number) => void;
  isPlaying: boolean;
  showPlayerControls: boolean;
  onTogglePlayerControls: () => void;
  playbackSpeed: number;
  onNavigateBack: () => void;
  onSearchAssetSelect: React.Dispatch<React.SetStateAction<string>>;
  disabledCompare: boolean;
}

const slotToTime = (slot: number): string => {
  const h = Math.floor(slot / 4);
  const m = (slot % 4) * 15;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
};

const slotToHourDecimalLabel = (slot: number): string => {
  const clampedSlot = Math.max(0, Math.floor(slot));
  const h = Math.floor(clampedSlot / 4);
  const m = (clampedSlot % 4) * 15;
  return `${h.toString().padStart(2, "0")}.${m.toString().padStart(2, "0")}`;
};

// Convert decimal time (e.g. 1.15, 23.45) back to nearest slot
const decimalToSlot = (val: number, maxHourRange: number = 95): number => {
  const h = Math.floor(val);
  const m = Math.round((val % 1) * 100);
  const clampedM = Math.min(45, Math.max(0, Math.round(m / 15) * 15));
  return Math.min(maxHourRange, h * 4 + clampedM / 15);
};

// Parse time string like "3:45" or "03:45" into nearest slot (0-95)
const timeStringToSlot = (val: string, maxHourRange: number = 95): number => {
  const s = val.trim();
  const match = s.match(/^(\d{1,2}):(\d{1,2})$/);
  maxHourRange = Math.min(95, Math.max(maxHourRange, 0)); // Ensure maxHourRange is between 0 and 95
  const maxHour = Math.min(23, Math.max(0, Math.floor(maxHourRange / 4))); // Ensure maxHour is between 0 and 23
  const maxMinute = Math.min(45, Math.max(0, (maxHourRange % 4) * 15)); // Ensure maxMinute is between 0 and 45

  if (match) {
    let h = Number(match[1]);
    let m = Number(match[2]);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return 0;
    if (h < 0) h = 0;
    if (h > maxHour) h = maxHour;
    // Round minutes to nearest 15
    m = Math.round(m / 15) * 15;
    if (m < 0) m = 0;
    if (h === maxHour && m > maxMinute) {
      m = maxMinute;
    } else if (m > 45) {
      m = 45;
    }
    return Math.min(95, h * 4 + m / 15);
  }
  // fallback: try decimal format (e.g. 3.45)
  const num = Number(s);
  if (!Number.isNaN(num)) return decimalToSlot(num, maxHourRange);
  return 0;
};

const AnalyticsHeaderComponent: React.FC<AnalyticsHeaderProps> = ({
  viewType,
  onViewTypeChange,
  date,
  onDateChange,
  minDate,
  maxDate,
  selectedAssetCount,
  isSelectedListVisible,
  onToggleSelectedList,
  onCompare,
  maxHourRange,
  hourRange,
  onHourRangeChange,
  isPlaying,
  showPlayerControls,
  onTogglePlayerControls,
  playbackSpeed,
  onNavigateBack,
  onSearchAssetSelect,
  disabledCompare,
}) => {
  // Determine primary view category
  const isAnalyticsView = viewType === FloorAnalyticsView.ANALYTICS_VIEW;
  const primaryView = isAnalyticsView ? "analytics" : "floorplan";

  // Determine secondary view (for floor plan mode)
  const floorPlanSubView =
    viewType === FloorAnalyticsView.PLAYBACK_VIEW_MOTION ? "motion" : "lightstatus";

  // UI labels for the time slider (design-only)
  const sliderMinLabel = "00";
  // maxHourRange is in 15-min slots; 4 slots = 1 hour
  const sliderMaxLabel = slotToHourDecimalLabel(maxHourRange);

  const handlePrimaryViewChange = (value: string) => {
    if (value === "analytics") {
      onViewTypeChange(FloorAnalyticsView.ANALYTICS_VIEW);
    } else {
      // Default to Light Operation Status when switching to Floor Plan View
      onViewTypeChange(FloorAnalyticsView.PLAYBACK_VIEW_LIGHT_STATUS);
    }
  };

  const handleFloorPlanSubViewChange = (value: string) => {
    if (value === "motion") {
      onViewTypeChange(FloorAnalyticsView.PLAYBACK_VIEW_MOTION);
    } else {
      onViewTypeChange(FloorAnalyticsView.PLAYBACK_VIEW_LIGHT_STATUS);
    }
  };

  return (
    <div className="analytics-header mb-3">
      <div className="analytics-header__back">
        <B2Button
          variant="outline-primary"
          className="d-block cursor-pointer analytics-header__button"
          onClick={onNavigateBack}
        >
          <FontAwesomeIcon
            title="Back to Floor Plan View"
            icon={faArrowLeft}
            className="fs-7 me-2"
          />
        </B2Button>
      </div>

      <div className="analytics-header__field analytics-header__field--select">
        <B2Form>
          <Form.Select
            onChange={(e: any) => handlePrimaryViewChange(e.target.value)}
            value={primaryView}
          >
            <option value="analytics">Analytics View</option>
            <option value="floorplan">Playback View</option>
          </Form.Select>
        </B2Form>
      </div>

      {!isAnalyticsView && (
        <div className="analytics-header__field analytics-header__field--select">
          <B2Form>
            <Form.Select
              onChange={(e: any) =>
                handleFloorPlanSubViewChange(e.target.value)
              }
              value={floorPlanSubView}
            >
              <option value="motion">Motion</option>
              <option value="lightstatus">Light Status</option>
            </Form.Select>
          </B2Form>
        </div>
      )}

      <div className="analytics-header__field analytics-header__field--search">
        <div className="analytics-header__search">
          <DashboardFloorplanWildcardSearchingComponent
            setSelectedSearchedAssetCodeForHighlighting={onSearchAssetSelect}
            searchBarLength="100%"
          />
        </div>
      </div>

      <div className="analytics-header__field analytics-header__field--date">
        <Form.Control
          type="date"
          placeholder="Select Date"
          onChange={(e) => onDateChange(e.target.value)}
          value={date}
          min={minDate}
          max={maxDate}
          className="analytics-header__date-input"
        />
      </div>

      {isAnalyticsView ? (
        <div className="analytics-header__actions analytics-header__actions--analytics">
          <B2Button
            variant={`${isSelectedListVisible && selectedAssetCount > 0
              ? "secondary"
              : "outline-secondary"
              }`}
            className="d-block cursor-pointer analytics-header__button"
            onClick={onToggleSelectedList}
            title="Show Selected Assets"
          >
            <FontAwesomeIcon icon={faList} className="me-2" />
            Selected ({selectedAssetCount})
          </B2Button>

          <B2Button
            variant="outline-primary"
            className="d-block cursor-pointer analytics-header__button"
            onClick={onCompare}
            disabled={disabledCompare}
          >
            Compare
          </B2Button>
        </div>
      ) : (
        <div className="analytics-header__actions analytics-header__actions--player">
          <div className="analytics-header__player-row">
            <div className="analytics-header__time-slider">
              <div className="analytics-header__time-control">
                <span className="analytics-header__time-prefix">TIME</span>
                <Form.Control
                  type="text"
                  value={slotToTime(hourRange)}
                  onChange={(e) => {
                    const slot = timeStringToSlot(e.target.value, maxHourRange);
                    onHourRangeChange(slot);
                  }}
                  className="analytics-header__time-input"
                />
                <span className="analytics-header__time-label">hr(s)</span>
              </div>

              <div className="analytics-header__slider-wrap">
                <span className="analytics-header__slider-label analytics-header__slider-label--min">
                  {sliderMinLabel}
                </span>
                <Form.Range
                  min={0}
                  max={maxHourRange}
                  step={1}
                  value={hourRange}
                  onChange={(e) => onHourRangeChange(Number(e.target.value))}
                  className="analytics-header__slider"
                />
                <span className="analytics-header__slider-label analytics-header__slider-label--max">
                  {sliderMaxLabel}
                </span>
              </div>
            </div>

            <B2Button
              variant={`${isPlaying ? "warning" : "primary"}`}
              className="cursor-pointer analytics-header__button analytics-header__animate-btn"
              onClick={onTogglePlayerControls}
              title={
                isPlaying
                  ? "Playing - Click to show controls"
                  : "Toggle Player Controls"
              }
            >
              <span
                className="analytics-header__animate-icon"
                aria-hidden="true"
              >
                <FontAwesomeIcon icon={faPlay} />
              </span>
              <span className="analytics-header__animate-text">
                Animate {isPlaying && `(${playbackSpeed}x)`}
              </span>
            </B2Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsHeaderComponent;
